import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useI18n } from '../i18n/index';

/*
 * DescentProfileAnimation
 *
 * Shows a small SVG cross-section of the descent path:
 *   - Dashed descent line angled to match the real glide slope
 *   - Mini aircraft silhouette animated along that line with SVG animateMotion
 *   - Altitude dot markers at start and end
 *   - Glide slope angle label
 *
 * The aircraft rotates automatically to match the descent angle (rotate="auto").
 * Hidden when there is no valid result. Respects prefers-reduced-motion.
 */
const DescentProfileAnimation = ({
  glideSlope,
  startAltitude,
  endAltitude,
  hasResult,
}) => {
  const { t } = useI18n();

  // Parse glide slope — clamp to a visible range
  const gs = Math.max(
    0.5,
    Math.min(parseFloat(String(glideSlope || '3').replace(/\s+/g, '')) || 3, 12)
  );

  // SVG canvas
  const W = 420, H = 88;
  const sx = 18, sy = 22;       // start point (top-left, high altitude)
  const ex = W - 18;             // end x

  // Visual drop: 3° ≈ 32px, 6° ≈ 55px — intentionally exaggerated for clarity
  const DROP_SCALE = 9;
  const drop = Math.round(Math.min(gs * DROP_SCALE, H - sy - 14));
  const ey = sy + drop;

  const pathD = `M ${sx},${sy} L ${ex},${ey}`;

  // Format displayed altitude labels (strip spaces / formatting)
  const fmtAlt = (v) => {
    const n = parseInt(String(v || '').replace(/\s+/g, ''), 10);
    return isNaN(n) ? '' : n.toLocaleString();
  };

  const startLabel = fmtAlt(startAltitude);
  const endLabel   = fmtAlt(endAltitude);

  if (!hasResult) return null;

  return (
    <Wrapper aria-hidden="true" title={`${t('form.glideSlope')}: ${gs}°`}>
      <ProfileSVG
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* ── Ground line ── */}
        <line
          x1={0} y1={H - 8}
          x2={W} y2={H - 8}
          stroke="rgba(59,130,246,0.12)"
          strokeWidth="1"
        />

        {/* ── Altitude band – very subtle fill ── */}
        <rect
          x={0} y={0}
          width={W} height={H - 8}
          fill="rgba(9,18,32,0.0)"
        />

        {/* ── Descent path – dashed ── */}
        <path
          d={pathD}
          stroke="rgba(59,130,246,0.35)"
          strokeWidth="1.5"
          strokeDasharray="7 5"
          fill="none"
        />

        {/* ── Altitude dot markers ── */}
        <circle cx={sx} cy={sy} r="3" fill="rgba(96,165,250,0.7)" />
        <circle cx={ex} cy={ey} r="3" fill="rgba(96,165,250,0.5)" />

        {/* ── Altitude labels ── */}
        {startLabel && (
          <text
            x={sx + 6} y={sy - 6}
            fontSize="8"
            fill="rgba(122,144,176,0.8)"
            fontFamily="JetBrains Mono, monospace"
          >
            {startLabel} {t('units.feet')}
          </text>
        )}
        {endLabel && (
          <text
            x={ex - 6} y={ey + 14}
            fontSize="8"
            fill="rgba(122,144,176,0.7)"
            fontFamily="JetBrains Mono, monospace"
            textAnchor="end"
          >
            {endLabel} {t('units.feet')}
          </text>
        )}

        {/* ── Glide slope angle label ── */}
        <text
          x={sx + (ex - sx) / 2}
          y={sy + drop / 2 - 7}
          fontSize="8"
          fill="rgba(96,165,250,0.6)"
          fontFamily="JetBrains Mono, monospace"
          textAnchor="middle"
        >
          {gs}{t('units.degrees')}
        </text>

        {/* ── Animated mini aircraft ── */}
        {/*
            The aircraft group is centered at (0,0).
            animateMotion places (0,0) along pathD.
            rotate="auto" tilts the aircraft to match the path direction.
        */}
        <g opacity="0">
          {/* fuselage */}
          <path
            d="M -15,0 Q -10,-2 8,-2 Q 14,-2 17,0 Q 14,2 8,2 Q -10,2 -15,0 Z"
            fill="rgba(190,218,255,0.88)"
          />
          {/* cockpit */}
          <ellipse cx="14" cy="0" rx="3" ry="1.8" fill="rgba(100,160,255,0.55)" />
          {/* wings upper */}
          <polygon points="2,-1.5 -5,-9 -8,-8 -1.5,0" fill="rgba(165,200,250,0.78)" />
          {/* wings lower */}
          <polygon points="2,1.5 -5,9 -8,8 -1.5,0" fill="rgba(165,200,250,0.78)" />
          {/* engine */}
          <rect x="-7" y="1.5" width="8" height="4" rx="2" fill="rgba(130,170,220,0.65)" />
          {/* vertical tail */}
          <polygon points="-11,0 -13.5,-4.5 -10,-0.5" fill="rgba(165,200,250,0.75)" />
          {/* horizontal tail upper */}
          <polygon points="-10.5,0 -15,-3 -13.5,-1.5 -10.5,0" fill="rgba(165,200,250,0.7)" />
          {/* horizontal tail lower */}
          <polygon points="-10.5,0 -15,3 -13.5,1.5 -10.5,0" fill="rgba(165,200,250,0.7)" />

          {/* fade in → cruise → fade out */}
          <animate
            attributeName="opacity"
            values="0;0;0.85;0.85;0"
            keyTimes="0;0.04;0.08;0.92;1"
            dur="7s"
            repeatCount="indefinite"
          />
          {/* move along the descent path */}
          <animateMotion
            path={pathD}
            dur="7s"
            repeatCount="indefinite"
            rotate="auto"
            calcMode="linear"
          />
        </g>
      </ProfileSVG>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  border-top: 1px solid ${theme.colors.borderSoft};
  padding-top: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};

  /* Hide entirely when user prefers reduced motion */
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const ProfileSVG = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
`;

export default DescentProfileAnimation;
